import Service from "../service";

/**
 * Approval workflow API wrapper.
 *
 * Uses the shared Service.makeAPICall so the auth token, company context and
 * refresh interceptors apply automatically.
 *
 * The server envelope is { status: configs.success_status (200) | configs.error_status (403),
 * data: {}, message: "" } - this project's real convention (see configs/index.js), not the
 * 1|0 shorthand CLAUDE.md describes. These helpers return that envelope untouched rather than
 * unwrapping to `data`, because the config screens need `status` and `message` - a validation
 * failure comes back as the error status with the specific reason, and that reason is what the
 * builder shows.
 */

const BASE = "/approval-workflow";
const RUNTIME_BASE = "/approvals";

const call = async (methodName, api_url, body) => {
  const response = await Service.makeAPICall({ methodName, api_url, body });
  return response?.data || { status: 403, message: "No response from server" };
};

const approvalApi = {
  /** Static dropdown data for the builder: modules, roles, rules, effects. */
  getMeta: () => call(Service.getMethod, `${BASE}/meta`),

  /** One row per module - configured or not - for the settings table. */
  listWorkflows: (params) => call(Service.getMethod, BASE, params),

  /** The stored workflow for one module. status 0 + reason NOT_CONFIGURED when absent. */
  getWorkflowByModule: (moduleKey) => call(Service.getMethod, `${BASE}/${moduleKey}`),

  /**
   * One specific workflow by its own _id - the only way to load a scoped
   * sibling (getWorkflowByModule always answers with the module's Default).
   */
  getWorkflowById: (id) => call(Service.getMethod, `${BASE}/id/${id}`),

  /**
   * Which override questions apply to a draft chain. Pure server-side derivation -
   * the builder re-calls this on every level edit so the override truth table is
   * never duplicated in client logic.
   */
  getCapabilities: (stages) => call(Service.postMethod, `${BASE}/capabilities`, { stages }),

  /** Create or replace a module's workflow. Idempotent per (company, module). */
  saveWorkflow: (payload) => call(Service.postMethod, BASE, payload),

  /** Update a workflow addressed by its own id. */
  updateWorkflow: (id, payload) => call(Service.putMethod, `${BASE}/${id}`, payload),

  /** Soft delete - the module falls back to its legacy single approval. */
  deleteWorkflow: (id) => call(Service.deleteMethod, `${BASE}/${id}`),

  /**
   * Resolve a chain against a real employee. Pass draft stages to check a chain
   * before saving it, or just a moduleKey to preview what is stored.
   */
  previewWorkflow: ({ moduleKey, employeeId, stages, settings, workflowId, scope }) =>
    call(Service.postMethod, `${BASE}/preview`, {
      moduleKey,
      employeeId,
      stages,
      settings,
      workflowId,
      scope,
    }),

  /** Route live traffic for one module onto the engine, or back off it. */
  setEngineEnabled: (moduleKey, enabled) =>
    call(Service.patchMethod, `${BASE}/${moduleKey}/engine`, { enabled }),

  /**
   * Re-map a module's scoped workflows onto a different dimension (docs/
   * approval-workflow/18-scoped-workflows.md §5.1) - every currently-scoped
   * sibling must be reassigned in the same call or the whole thing is rejected.
   */
  switchScopeDimension: (moduleKey, { fromDimension, newDimension, reassignments }) =>
    call(Service.postMethod, `${BASE}/${moduleKey}/switch-scope-dimension`, { fromDimension, newDimension, reassignments }),

  /**
   * Which scope dimension wins for this module when an applicant matches
   * scoped workflows on more than one at once.
   */
  getScopePriority: (moduleKey) => call(Service.getMethod, `${BASE}/${moduleKey}/scope-priority`),

  /** Set the dimension priority order for a module. */
  setScopePriority: (moduleKey, priority) =>
    call(Service.putMethod, `${BASE}/${moduleKey}/scope-priority`, { priority }),

  /**
   * How many open requests still carry override grants from an older version of
   * the settings. Read-only - used to decide whether offering the "apply" action
   * would do anything at all.
   */
  getOverrideSyncPreview: (moduleKey) =>
    call(Service.getMethod, `${BASE}/${moduleKey}/override-sync`),

  /**
   * Apply the current override settings to this module's open requests.
   *
   * A workflow edit deliberately does NOT reach requests already in flight -
   * their approval chain is frozen the day they are raised. This is the explicit
   * opt-in. Approval levels are never changed, only who may override them.
   */
  applyOverrideSync: (moduleKey) =>
    call(Service.postMethod, `${BASE}/${moduleKey}/override-sync`, {}),

  /**
   * How many requests are in progress with **no chain at all** - the ones raised
   * before the module was switched on. Read-only.
   *
   * Not the same question as `getOverrideSyncPreview`: that one is about
   * requests that have a chain but froze their override rules earlier. This one
   * is about requests that have no chain to advance, and so are still decided by
   * a single approver no matter how many levels the settings show.
   */
  getChainBackfillPreview: (moduleKey) =>
    call(Service.getMethod, `${BASE}/${moduleKey}/chain-backfill`),

  /** Open a chain for every still-open request of this module that has none. */
  applyChainBackfill: (moduleKey) =>
    call(Service.postMethod, `${BASE}/${moduleKey}/chain-backfill`, {}),

  /**
   * How many still-untouched PENDING requests (Level 1, zero decisions) are on
   * a chain that no longer matches what the engine would resolve today - a
   * scope edit (branch added to an existing Branch-scoped workflow, priority
   * reordered, ...) left them pointing at the wrong one. Distinct from
   * chain-backfill: that is for requests with NO chain; this is for requests
   * whose existing chain has gone stale.
   */
  getChainRescopePreview: (moduleKey) =>
    call(Service.getMethod, `${BASE}/${moduleKey}/chain-rescope`),

  /** Move every mismatched, still-untouched request onto its now-correct workflow. */
  applyChainRescope: (moduleKey) =>
    call(Service.postMethod, `${BASE}/${moduleKey}/chain-rescope`, {}),

  // ── Runtime ────────────────────────────────────────────────────────────

  /** Dashboard badge + inbox tab counts. primaryTotal and overrideTotal are disjoint. */
  getCounts: () => call(Service.getMethod, `${RUNTIME_BASE}/counts`),

  /** The approver's inbox: requests the caller may act on right now. */
  getPending: (params) => call(Service.getMethod, `${RUNTIME_BASE}/pending`, params),

  /** The applicant's own requests, each carrying approval_progress. */
  getMyRequests: (params) => call(Service.getMethod, `${RUNTIME_BASE}/my-requests`, params),

  /** Full detail for one request: every level, every decision, the history. */
  getDetail: (requestId) => call(Service.getMethod, `${RUNTIME_BASE}/${requestId}`),

  /** Timeline for a module's own detail drawer - the one call module screens make directly. */
  getByEntity: (moduleKey, entityId, entitySubKey) =>
    call(
      Service.getMethod,
      `${RUNTIME_BASE}/by-entity/${moduleKey}/${entityId}`,
      entitySubKey ? { entitySubKey } : undefined
    ),

  /**
   * Approve / reject / send back.
   *
   * `partnerId` is optional and only meaningful for a SHIFT_SWAP module's
   * pending SWAP request — the generic engine's approve payload is extended
   * (docs/swap-shift/05-api-and-routes.md Phase 5 step 3) to forward it
   * through to `shiftSwap.adapter.js`. Every other caller omits it, and
   * `undefined` is dropped from the JSON body, so this is a no-op for them.
   */
  postAction: (requestId, { action, comment, partnerId }) =>
    call(Service.postMethod, `${RUNTIME_BASE}/${requestId}/action`, { action, comment, partnerId }),

  /** The gate runs per request - partial success is normal and expected. */
  postBulkAction: ({ requestIds, action, comment }) =>
    call(Service.postMethod, `${RUNTIME_BASE}/bulk-action`, { requestIds, action, comment }),

  /** Applicant withdraws their own pending request. */
  postWithdraw: (requestId) => call(Service.postMethod, `${RUNTIME_BASE}/${requestId}/withdraw`),

  /**
   * Admin-only, read-only preview of exactly which levels a "discard progress,
   * restart at Level 1" would throw away for this one request - safe to call
   * every time the confirm modal renders.
   */
  getRestartPreview: (requestId) =>
    call(Service.getMethod, `${RUNTIME_BASE}/${requestId}/restart-preview`),

  /**
   * Admin-only. Closes this request's current chain and opens a brand-new one
   * at Level 1 against today's live workflow configuration, discarding
   * whatever progress the old chain had. Refuses without
   * `confirmDiscardProgress: true`.
   */
  postRestart: (requestId, { confirmDiscardProgress }) =>
    call(Service.postMethod, `${RUNTIME_BASE}/${requestId}/restart`, { confirmDiscardProgress }),
};

export default approvalApi;
